import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useToast } from "@/hooks/use-toast";
import { useUserRole } from "@/hooks/useUserRole";
import {
  Loader2, Copy, CheckCircle2, Clock, ExternalLink, AlertTriangle,
  ChevronDown, ChevronUp, ShieldCheck,
} from "lucide-react";

type PixData = {
  paymentId: string;
  mpPaymentId: string;
  status: "pending" | "succeeded" | "failed" | "expired" | "refunded";
  amount: number;
  currency: string;
  qrCode: string | null;
  qrCodeBase64: string | null;
  ticketUrl: string | null;
  expiresAt: string | null;
};

type PixDiagnostics = {
  mpStatus: string | null;
  mpStatusDetail: string | null;
  mpPaymentId: string | number | null;
  mpPaymentMethodId: string | null;
  mpPaymentTypeId: string | null;
  mpDateLastUpdated: string | null;
  mpDateOfExpiration: string | null;
  sentDateOfExpiration: string | null;
  expirationMismatch: boolean | null;
  mpCollectorId: string | number | null;
  mpLiveMode: boolean | null;
  sentPayer: Record<string, boolean> | null;
  createdAt: number;
  failedAt: number | null;
  confirmedFinal: boolean;
};

export type PixPanelCourse = {
  id: string;
  title: string;
  slug: string | null;
  price: number | null;
};

const POLL_MS = 5000;
const RETRY_BLOCK_KEY = "pix_retry_block_until";
const RETRY_LOG_KEY = "pix_recent_failures";
const RETRY_FAST_FAIL_MS = 8000;
const RETRY_FAST_FAIL_LIMIT = 3;
const RETRY_BLOCK_MS = 10 * 60 * 1000;
const ADMIN_OVERRIDE_MIN = 0.5;
const ADMIN_OVERRIDE_MAX = 1000;

const FAST_FAIL_THRESHOLD_MS = 60_000;

function friendlyFailureReason(
  detail: string | null | undefined,
  elapsedMs?: number | null,
): { title: string; description: string; tag: string } {
  const d = (detail ?? "").toLowerCase();

  // Specific status_detail values per Mercado Pago Pix docs:
  if (d === "by_payer" || d.includes("by_payer")) {
    return {
      title: "Pagamento cancelado pelo pagador",
      description:
        "Você (ou o titular da conta) cancelou este Pix no app do banco. Gere um novo código se quiser tentar novamente.",
      tag: "by_payer",
    };
  }
  if (d === "by_collector" || d.includes("by_collector")) {
    return {
      title: "Pagamento cancelado pelo recebedor",
      description:
        "A cobrança foi cancelada pela conta vendedora ou pela nossa integração. Se isto persistir, é configuração do recebedor — fale com o suporte.",
      tag: "by_collector",
    };
  }
  if (d.includes("rejected_by_bank") || d.includes("bank_error")) {
    return {
      title: "Pagamento recusado pelo banco",
      description: "Seu banco recusou esta transação Pix.",
      tag: "bank_error",
    };
  }
  if (d.includes("rejected_high_risk")) {
    return {
      title: "Pagamento recusado por risco",
      description: "O antifraude do Mercado Pago bloqueou esta transação.",
      tag: "high_risk",
    };
  }
  if (d.includes("insufficient")) {
    return {
      title: "Saldo insuficiente",
      description: "Não há saldo suficiente para completar o Pix.",
      tag: "insufficient",
    };
  }
  if (d === "expired" || d.includes("expired")) {
    // Two scenarios share status_detail=expired:
    //  - Natural expiration: payer didn't finish in the bank app within ~30min.
    //  - Fast-fail: MP cancels in seconds — usually receiver account problem.
    const isFastFail =
      typeof elapsedMs === "number" && elapsedMs > 0 && elapsedMs < FAST_FAIL_THRESHOLD_MS;
    if (isFastFail) {
      return {
        title: "O código Pix foi cancelado pelo provedor",
        description:
          "O Mercado Pago invalidou este código Pix em poucos segundos. Costuma indicar problema com a conta do recebedor (homologação Pix, chave cadastrada ou limites). Tente novamente em alguns minutos — se persistir, avise o suporte.",
        tag: "fast_fail",
      };
    }
    return {
      title: "O código Pix expirou",
      description:
        "O prazo para pagar este código Pix terminou antes da confirmação. Gere um novo código e finalize no app do seu banco em até 30 minutos.",
      tag: "expired_natural",
    };
  }
  if (d.includes("cc_rejected")) {
    return {
      title: "Pagamento recusado",
      description: "O provedor recusou esta transação. Tente novamente.",
      tag: "rejected",
    };
  }
  return {
    title: "Pagamento não concluído",
    description: "O pagamento não foi confirmado pelo Mercado Pago. Você pode tentar gerar um novo código Pix.",
    tag: "unknown",
  };
}

function readRetryBlock(): number {
  try {
    const v = Number(localStorage.getItem(RETRY_BLOCK_KEY) ?? "0");
    return Number.isFinite(v) ? v : 0;
  } catch {
    return 0;
  }
}
function recordFastFailure() {
  try {
    const arr: number[] = JSON.parse(localStorage.getItem(RETRY_LOG_KEY) ?? "[]");
    const now = Date.now();
    const recent = [...arr, now].filter((t) => now - t < 30 * 60 * 1000);
    localStorage.setItem(RETRY_LOG_KEY, JSON.stringify(recent));
    if (recent.length >= RETRY_FAST_FAIL_LIMIT) {
      localStorage.setItem(RETRY_BLOCK_KEY, String(now + RETRY_BLOCK_MS));
    }
  } catch {
    /* ignore */
  }
}

interface PixCheckoutPanelProps {
  course: PixPanelCourse;
  /** Render without outer Card wrapper (e.g. inside a Dialog body). */
  embedded?: boolean;
  /** Called when payment succeeds (before auto-navigation). */
  onPaid?: () => void;
  /** If false, do not auto-navigate to /learn on success. */
  navigateOnSuccess?: boolean;
}

export function PixCheckoutPanel({
  course,
  embedded = false,
  onPaid,
  navigateOnSuccess = true,
}: PixCheckoutPanelProps) {
  const navigate = useNavigate();
  const { toast } = useToast();
  const { isAdmin, roles } = useUserRole();
  const isStaff = isAdmin || roles?.includes("instructor");

  const [pix, setPix] = useState<PixData | null>(null);
  const [creating, setCreating] = useState(false);
  const [copied, setCopied] = useState(false);
  const [now, setNow] = useState(() => Date.now());
  const [cpf, setCpf] = useState("");
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [phone, setPhone] = useState("");
  const [zipCode, setZipCode] = useState("");
  const [streetName, setStreetName] = useState("");
  const [streetNumber, setStreetNumber] = useState("");
  const [showAddress, setShowAddress] = useState(false);
  const [diag, setDiag] = useState<PixDiagnostics | null>(null);
  const [showDiag, setShowDiag] = useState(false);
  const [retryBlockedUntil, setRetryBlockedUntil] = useState<number>(() => readRetryBlock());
  const [deviceId, setDeviceId] = useState<string | null>(null);
  // Admin override
  const [adminOverrideEnabled, setAdminOverrideEnabled] = useState(false);
  const [adminOverrideValue, setAdminOverrideValue] = useState<string>("5.00");

  // Load Mercado Pago security SDK for device ID
  useEffect(() => {
    const SCRIPT_ID = "mp-security-sdk";
    const tryRead = () => {
      let tries = 0;
      const t = window.setInterval(() => {
        const id = (window as any).MP_DEVICE_SESSION_ID as string | undefined;
        if (id) { setDeviceId(id); window.clearInterval(t); }
        if (++tries > 20) window.clearInterval(t);
      }, 250);
      return () => window.clearInterval(t);
    };
    if (document.getElementById(SCRIPT_ID)) return tryRead();
    const s = document.createElement("script");
    s.id = SCRIPT_ID;
    s.src = "https://www.mercadopago.com/v2/security.js";
    s.setAttribute("view", "checkout");
    s.async = true;
    s.onload = tryRead;
    document.head.appendChild(s);
  }, []);

  const pollRef = useRef<number | null>(null);

  // Prefill name
  useEffect(() => {
    let active = true;
    (async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user || !active) return;
      const { data: profile } = await supabase
        .from("profiles").select("full_name").eq("id", user.id).maybeSingle();
      if (!active) return;
      const fn = (profile?.full_name ?? "").trim();
      if (fn) {
        const [first, ...rest] = fn.split(/\s+/);
        if (first) setFirstName((cur) => cur || first);
        const last = rest.join(" ");
        if (last) setLastName((cur) => cur || last);
      }
    })();
    return () => { active = false; };
  }, []);

  useEffect(() => {
    const t = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(t);
  }, []);

  // Poll
  useEffect(() => {
    if (!pix || pix.status !== "pending") return;
    const tick = async () => {
      const { data, error } = await supabase.functions.invoke("check-pix-payment", {
        body: { paymentId: pix.paymentId },
      });
      if (error) { console.error(error); return; }
      const resp = data as any;
      let status = resp?.status as PixData["status"] | undefined;
      let finalDetail: string | null = resp?.mpStatusDetail ?? null;

      // LLM MP recommendation #2: don't trust the first webhook update.
      // If the polled status is a terminal failure, do ONE confirmation
      // re-check after a short delay to make sure status_detail isn't
      // an intermediate state. We compare the two reads — if they agree,
      // we proceed; if not, we keep the second read (most recent truth).
      if (status === "failed" || status === "expired") {
        await new Promise((r) => setTimeout(r, 1500));
        const { data: d2 } = await supabase.functions.invoke("check-pix-payment", {
          body: { paymentId: pix.paymentId },
        });
        const r2 = d2 as any;
        if (r2?.status) status = r2.status as PixData["status"];
        if (r2?.mpStatusDetail) finalDetail = r2.mpStatusDetail;
      }

      setDiag((prev) => ({
        mpStatus: resp?.mpStatus ?? prev?.mpStatus ?? null,
        mpStatusDetail: finalDetail ?? prev?.mpStatusDetail ?? null,
        mpPaymentId: resp?.mpPaymentId ?? prev?.mpPaymentId ?? pix.mpPaymentId ?? null,
        mpPaymentMethodId: resp?.mpPaymentMethodId ?? prev?.mpPaymentMethodId ?? null,
        mpPaymentTypeId: resp?.mpPaymentTypeId ?? prev?.mpPaymentTypeId ?? null,
        mpDateLastUpdated: resp?.mpDateLastUpdated ?? prev?.mpDateLastUpdated ?? null,
        mpDateOfExpiration: resp?.mpDateOfExpiration ?? prev?.mpDateOfExpiration ?? null,
        sentDateOfExpiration: prev?.sentDateOfExpiration ?? null,
        expirationMismatch: prev?.expirationMismatch ?? null,
        mpCollectorId: resp?.mpCollectorId ?? prev?.mpCollectorId ?? null,
        mpLiveMode: resp?.mpLiveMode ?? prev?.mpLiveMode ?? null,
        sentPayer: resp?.sentPayer ?? prev?.sentPayer ?? null,
        createdAt: prev?.createdAt ?? Date.now(),
        failedAt: prev?.failedAt ?? null,
        confirmedFinal: status === "succeeded" || status === "failed" || status === "expired",
      }));
      if (status && status !== pix.status) {
        setPix({ ...pix, status });
        if (status === "succeeded") {
          toast({ title: "Pagamento confirmado", description: "Matriculando você no curso..." });
          onPaid?.();
          if (navigateOnSuccess) {
            setTimeout(() => {
              if (course?.slug) navigate(`/learn/${course.slug}`);
              else navigate("/dashboard");
            }, 1200);
          }
        } else if (status === "expired" || status === "failed") {
          setDiag((prev) => {
            const createdAt = prev?.createdAt ?? Date.now();
            const failedAt = Date.now();
            const elapsed = failedAt - createdAt;
            const reason = friendlyFailureReason(finalDetail, elapsed);
            toast({ title: reason.title, description: reason.description, variant: "destructive" });
            // Only count as "fast-fail for retry block" if MP itself bailed
            // very quickly (likely receiver account problem). Natural
            // expirations and explicit user/bank cancels should NOT block.
            if (
              elapsed < RETRY_FAST_FAIL_MS &&
              reason.tag !== "by_payer" &&
              reason.tag !== "expired_natural"
            ) {
              recordFastFailure();
              setRetryBlockedUntil(readRetryBlock());
            }
            return prev ? { ...prev, failedAt } : prev;
          });
        }
      }
    };
    pollRef.current = window.setInterval(tick, POLL_MS);
    return () => { if (pollRef.current) window.clearInterval(pollRef.current); };
  }, [pix, course, navigate, toast, onPaid, navigateOnSuccess]);

  const expiresInSec = useMemo(() => {
    if (!pix?.expiresAt) return null;
    return Math.max(0, Math.floor((new Date(pix.expiresAt).getTime() - now) / 1000));
  }, [pix, now]);

  const isRetryBlocked = retryBlockedUntil > Date.now();
  const retryBlockMinutesLeft = isRetryBlocked
    ? Math.max(1, Math.ceil((retryBlockedUntil - Date.now()) / 60000))
    : 0;

  const startCheckout = async () => {
    if (!course) return;
    if (!isStaff && readRetryBlock() > Date.now()) {
      setRetryBlockedUntil(readRetryBlock());
      toast({
        title: "Pagamentos via Pix temporariamente indisponíveis",
        description: "Estamos corrigindo um problema com nosso provedor. Tente novamente em alguns minutos.",
        variant: "destructive",
      });
      return;
    }
    setCreating(true);
    try {
      const sessionRes = await supabase.auth.getSession();
      if (!sessionRes.data.session) {
        navigate(`/auth?redirect=/courses/${course.slug ?? course.id}`);
        return;
      }
      const cleanCpf = cpf.replace(/\D/g, "");
      if (cleanCpf.length !== 11) {
        toast({ title: "CPF obrigatório", description: "Informe um CPF válido (11 dígitos).", variant: "destructive" });
        setCreating(false); return;
      }
      if (!firstName.trim() || !lastName.trim()) {
        toast({
          title: "Nome e sobrenome obrigatórios",
          description: "O Mercado Pago exige nome e sobrenome do comprador.",
          variant: "destructive",
        });
        setCreating(false); return;
      }
      const cleanPhone = phone.replace(/\D/g, "");
      const body: Record<string, unknown> = {
        courseId: course.id,
        payerCpf: cleanCpf,
        payerFirstName: firstName.trim(),
        payerLastName: lastName.trim(),
      };
      if (cleanPhone.length >= 10 && cleanPhone.length <= 11) {
        body.payerPhoneArea = cleanPhone.slice(0, 2);
        body.payerPhoneNumber = cleanPhone.slice(2);
      }
      const liveDeviceId =
        deviceId || ((window as any).MP_DEVICE_SESSION_ID as string | undefined) || null;
      if (liveDeviceId) body.deviceId = liveDeviceId;

      const cleanZip = zipCode.replace(/\D/g, "");
      const trimmedStreet = streetName.trim();
      const trimmedNumber = streetNumber.trim();
      if (cleanZip.length === 8 && trimmedStreet && trimmedNumber) {
        body.payerZipCode = cleanZip;
        body.payerStreetName = trimmedStreet.slice(0, 120);
        body.payerStreetNumber = trimmedNumber.slice(0, 20);
      }

      // Admin-only override
      if (isStaff && adminOverrideEnabled) {
        const v = Number(adminOverrideValue.replace(",", "."));
        if (!Number.isFinite(v) || v < ADMIN_OVERRIDE_MIN || v > ADMIN_OVERRIDE_MAX) {
          toast({
            title: "Valor de teste inválido",
            description: `Use um valor entre R$ ${ADMIN_OVERRIDE_MIN.toFixed(2)} e R$ ${ADMIN_OVERRIDE_MAX.toFixed(2)}.`,
            variant: "destructive",
          });
          setCreating(false); return;
        }
        body.discountedAmount = Number(v.toFixed(2));
      }

      const { data, error } = await supabase.functions.invoke("create-pix-payment", { body });
      if (error) throw error;
      const created = data as PixData & {
        mpStatus?: string | null;
        mpStatusDetail?: string | null;
        mpPaymentMethodId?: string | null;
        mpPaymentTypeId?: string | null;
        mpCollectorId?: string | number | null;
        mpLiveMode?: boolean | null;
        sentDateOfExpiration?: string | null;
        expirationMismatch?: boolean | null;
      };
      setPix(created);
      const createdAt = Date.now();
      const fastFailed = created.status === "failed" || created.status === "expired";
      setDiag({
        mpStatus: created.mpStatus ?? null,
        mpStatusDetail: created.mpStatusDetail ?? null,
        mpPaymentId: created.mpPaymentId ?? null,
        mpPaymentMethodId: created.mpPaymentMethodId ?? null,
        mpPaymentTypeId: created.mpPaymentTypeId ?? null,
        mpDateLastUpdated: null,
        mpDateOfExpiration: created.expiresAt ?? null,
        sentDateOfExpiration: created.sentDateOfExpiration ?? null,
        expirationMismatch: created.expirationMismatch ?? null,
        mpCollectorId: created.mpCollectorId ?? null,
        mpLiveMode: created.mpLiveMode ?? null,
        sentPayer: null,
        createdAt,
        // Fast-fail at creation: MP returned cancelled/expired in the very
        // first POST — stamp failed_at immediately so the diagnostic shows
        // elapsed ≈ 0ms and the UI skips the misleading "pending" flicker.
        failedAt: fastFailed ? createdAt : null,
        confirmedFinal: fastFailed,
      });
      if (fastFailed) {
        // elapsed === 0 here by definition (cancel happened at creation),
        // so this always renders the fast-fail copy.
        const reason = friendlyFailureReason(created.mpStatusDetail, 0);
        toast({ title: reason.title, description: reason.description, variant: "destructive" });
        recordFastFailure();
        setRetryBlockedUntil(readRetryBlock());
      }
    } catch (e: any) {
      console.error(e);
      toast({
        title: "Não foi possível iniciar o Pix",
        description: e?.message ?? "Tente novamente.",
        variant: "destructive",
      });
    } finally {
      setCreating(false);
    }
  };

  const copyCode = async () => {
    if (!pix?.qrCode) return;
    try {
      await navigator.clipboard.writeText(pix.qrCode);
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    } catch {
      toast({ title: "Copy failed", variant: "destructive" });
    }
  };

  const fastFailMs = diag?.failedAt && diag?.createdAt ? diag.failedAt - diag.createdAt : null;
  const failureReason = friendlyFailureReason(diag?.mpStatusDetail, fastFailMs);

  // ============ Form view ============
  const FormView = (
    <div className="space-y-4">
      {isRetryBlocked && !isStaff && (
        <div className="rounded-md border border-destructive/40 bg-destructive/10 p-3 text-sm flex gap-2">
          <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0 text-destructive" />
          <div>
            <p className="font-medium text-destructive">Pagamentos via Pix temporariamente indisponíveis</p>
            <p className="text-muted-foreground">
              Tente novamente em cerca de {retryBlockMinutesLeft} minuto{retryBlockMinutesLeft > 1 ? "s" : ""}.
            </p>
          </div>
        </div>
      )}

      <div className="grid grid-cols-2 gap-3">
        <div>
          <Label htmlFor="pix-firstName">Nome <span className="text-destructive">*</span></Label>
          <Input id="pix-firstName" value={firstName} onChange={(e) => setFirstName(e.target.value)} />
        </div>
        <div>
          <Label htmlFor="pix-lastName">Sobrenome <span className="text-destructive">*</span></Label>
          <Input id="pix-lastName" value={lastName} onChange={(e) => setLastName(e.target.value)} />
        </div>
      </div>
      <div>
        <Label htmlFor="pix-cpf">CPF <span className="text-destructive">*</span></Label>
        <Input
          id="pix-cpf" inputMode="numeric" placeholder="000.000.000-00"
          value={cpf} onChange={(e) => setCpf(e.target.value)} maxLength={14}
        />
        <p className="mt-1 text-xs text-muted-foreground">11 dígitos. Usado apenas para validação antifraude do Mercado Pago.</p>
      </div>
      <div>
        <Label htmlFor="pix-phone">Telefone (opcional)</Label>
        <Input
          id="pix-phone" inputMode="tel" placeholder="(11) 99999-9999"
          value={phone} onChange={(e) => setPhone(e.target.value)} maxLength={16}
        />
      </div>

      <button
        type="button"
        onClick={() => setShowAddress((v) => !v)}
        className="flex items-center gap-1 text-xs font-medium text-muted-foreground hover:text-foreground"
      >
        {showAddress ? <ChevronUp className="h-3.5 w-3.5" /> : <ChevronDown className="h-3.5 w-3.5" />}
        Endereço (opcional, aumenta a aprovação)
      </button>
      {showAddress && (
        <div className="space-y-3 rounded-md border border-border bg-muted/30 p-3">
          <div>
            <Label htmlFor="pix-zip">CEP</Label>
            <Input id="pix-zip" inputMode="numeric" placeholder="00000-000"
              value={zipCode} onChange={(e) => setZipCode(e.target.value)} maxLength={9} />
          </div>
          <div className="grid grid-cols-[1fr_120px] gap-3">
            <div>
              <Label htmlFor="pix-street">Rua</Label>
              <Input id="pix-street" value={streetName} onChange={(e) => setStreetName(e.target.value)} maxLength={120} />
            </div>
            <div>
              <Label htmlFor="pix-number">Número</Label>
              <Input id="pix-number" value={streetNumber} onChange={(e) => setStreetNumber(e.target.value)} maxLength={20} />
            </div>
          </div>
        </div>
      )}

      {/* Admin-only override */}
      {isStaff && (
        <div className="rounded-md border border-amber-500/40 bg-amber-500/5 p-3 space-y-2">
          <div className="flex items-center justify-between gap-3">
            <div>
              <p className="text-xs font-semibold text-amber-700 dark:text-amber-400">
                Modo de teste (admin/instrutor)
              </p>
              <p className="text-[11px] text-muted-foreground">
                Cobra um valor reduzido para validar o fluxo Pix em produção.
              </p>
            </div>
            <label className="flex items-center gap-2 text-xs">
              <input
                type="checkbox"
                checked={adminOverrideEnabled}
                onChange={(e) => setAdminOverrideEnabled(e.target.checked)}
              />
              Ativar
            </label>
          </div>
          {adminOverrideEnabled && (
            <div className="flex items-center gap-2">
              <Label htmlFor="pix-admin-amount" className="text-xs whitespace-nowrap">Valor (BRL)</Label>
              <Input
                id="pix-admin-amount"
                inputMode="decimal"
                value={adminOverrideValue}
                onChange={(e) => setAdminOverrideValue(e.target.value)}
                className="h-8"
              />
              <span className="text-[11px] text-muted-foreground whitespace-nowrap">
                R$ {ADMIN_OVERRIDE_MIN.toFixed(2)}–{ADMIN_OVERRIDE_MAX.toFixed(2)}
              </span>
            </div>
          )}
          <p className="text-[11px] font-mono text-muted-foreground">
            device_id: {deviceId ? `${deviceId.slice(0, 12)}… ✓` : "carregando… ✗"}
          </p>
        </div>
      )}

      <Button
        className="w-full"
        disabled={
          creating ||
          (isRetryBlocked && !isStaff) ||
          cpf.replace(/\D/g, "").length !== 11 ||
          !firstName.trim() ||
          !lastName.trim()
        }
        onClick={startCheckout}
      >
        {creating ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
        Gerar código Pix
      </Button>
    </div>
  );

  // ============ QR view ============
  const QRView = pix && (
    <div className="space-y-4">
      <div className="flex items-center gap-2">
        {pix.status === "succeeded" ? (
          <><CheckCircle2 className="h-5 w-5 text-emerald-500" /><span className="font-medium">Pagamento confirmado</span></>
        ) : pix.status === "expired" || pix.status === "failed" ? (
          <><AlertTriangle className="h-5 w-5 text-destructive" /><span className="font-medium">{failureReason.title}</span></>
        ) : (
          <><Clock className="h-5 w-5" /><span className="font-medium">Aguardando pagamento</span></>
        )}
      </div>
      <p className="text-sm text-muted-foreground">
        {pix.amount.toLocaleString("pt-BR", { style: "currency", currency: pix.currency || "BRL" })}
      </p>

      {pix.qrCodeBase64 && pix.status === "pending" && (
        <div className="flex justify-center">
          <img src={`data:image/png;base64,${pix.qrCodeBase64}`} alt="Pix QR code"
            className="w-56 h-56 rounded-md border bg-white p-2" />
        </div>
      )}

      {expiresInSec !== null && pix.status === "pending" && (
        <p className="text-center text-sm text-muted-foreground">
          Expira em {Math.floor(expiresInSec / 60)}:{String(expiresInSec % 60).padStart(2, "0")}
        </p>
      )}

      {pix.qrCode && pix.status === "pending" && (
        <div className="space-y-2">
          <Label>Código copia e cola</Label>
          <div className="flex gap-2">
            <Input readOnly value={pix.qrCode} className="font-mono text-xs" />
            <Button type="button" variant="outline" onClick={copyCode}>
              {copied ? <CheckCircle2 className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
            </Button>
          </div>
        </div>
      )}

      {pix.ticketUrl && pix.status === "pending" && (
        <Button asChild variant="outline" className="w-full">
          <a href={pix.ticketUrl} target="_blank" rel="noreferrer">
            Abrir no Mercado Pago <ExternalLink className="h-4 w-4 ml-2" />
          </a>
        </Button>
      )}

      {(pix.status === "expired" || pix.status === "failed") && (
        <>
          <p className="text-sm text-muted-foreground">{failureReason.description}</p>
          <Button className="w-full" disabled={isRetryBlocked && !isStaff}
            onClick={() => { setPix(null); setDiag(null); }}>
            Tentar novamente
          </Button>
        </>
      )}

      {pix.status === "succeeded" && (
        <Button className="w-full" onClick={() => navigate(course?.slug ? `/learn/${course.slug}` : "/dashboard")}>
          Ir para o curso
        </Button>
      )}

      {/* Trust footer */}
      <div className="flex items-center justify-center gap-2 text-[11px] text-muted-foreground pt-2 border-t border-border">
        <ShieldCheck className="h-3.5 w-3.5" />
        Transação segura processada pelo Mercado Pago
      </div>

      {/* Staff diagnostic */}
      {isStaff && diag && (pix.status === "failed" || pix.status === "expired") && (
        <div className="mt-2 rounded-md border border-border bg-muted/40">
          <button type="button"
            className="w-full flex items-center justify-between px-3 py-2 text-xs font-medium text-muted-foreground hover:text-foreground"
            onClick={() => setShowDiag((v) => !v)}>
            <span>Diagnóstico (apenas staff)</span>
            {showDiag ? <ChevronUp className="h-3.5 w-3.5" /> : <ChevronDown className="h-3.5 w-3.5" />}
          </button>
          {showDiag && (
            <dl className="px-3 pb-3 text-xs grid grid-cols-[auto,1fr] gap-x-3 gap-y-1 font-mono">
              <dt className="text-muted-foreground">mp_payment_id</dt><dd>{String(diag.mpPaymentId ?? "—")}</dd>
              <dt className="text-muted-foreground">mp_status</dt><dd>{diag.mpStatus ?? "—"}</dd>
              <dt className="text-muted-foreground">mp_status_detail</dt>
              <dd className="text-destructive">
                {diag.mpStatusDetail ?? "—"}
                {diag.confirmedFinal ? " ✓ confirmado" : " (não confirmado)"}
              </dd>
              <dt className="text-muted-foreground">payment_method_id</dt>
              <dd className={diag.mpPaymentMethodId && diag.mpPaymentMethodId !== "pix" ? "text-destructive" : ""}>
                enviado: pix · recebido: {diag.mpPaymentMethodId ?? "—"}
                {diag.mpPaymentTypeId ? ` (${diag.mpPaymentTypeId})` : ""}
              </dd>
              <dt className="text-muted-foreground">date_of_expiration</dt>
              <dd className={diag.expirationMismatch ? "text-destructive" : ""}>
                enviado: {diag.sentDateOfExpiration ?? "—"}
                <br />
                recebido: {diag.mpDateOfExpiration ?? "—"}
                {diag.expirationMismatch ? " ⚠️ divergente (<25 min)" : ""}
              </dd>
              <dt className="text-muted-foreground">created_at</dt><dd>{new Date(diag.createdAt).toISOString()}</dd>
              <dt className="text-muted-foreground">failed_at</dt>
              <dd>{diag.failedAt ? new Date(diag.failedAt).toISOString() : "—"}</dd>
              {fastFailMs !== null && (<>
                <dt className="text-muted-foreground">elapsed</dt>
                <dd className={fastFailMs < FAST_FAIL_THRESHOLD_MS ? "text-destructive" : ""}>
                  {fastFailMs} ms · tag: <strong>{failureReason.tag}</strong>
                  {failureReason.tag === "fast_fail"
                    ? " — MP cancelou em segundos; verificar conta/homologação Pix do recebedor"
                    : failureReason.tag === "expired_natural"
                    ? " — código Pix venceu antes do pagamento; comportamento esperado"
                    : ""}
                </dd>
              </>)}
              <dt className="text-muted-foreground">mp_collector_id</dt><dd>{String(diag.mpCollectorId ?? "—")}</dd>
              <dt className="text-muted-foreground">mp_live_mode</dt>
              <dd>{diag.mpLiveMode === null ? "—" : String(diag.mpLiveMode)}</dd>
              {diag.sentPayer && (<>
                <dt className="text-muted-foreground">sent_payer</dt>
                <dd className="text-xs">
                  {Object.entries(diag.sentPayer).map(([k, v]) => `${k}:${v ? "✓" : "✗"}`).join("  ")}
                </dd>
              </>)}
            </dl>
          )}
        </div>
      )}
    </div>
  );

  if (embedded) return pix ? QRView : FormView;

  return (
    <Card>
      <CardHeader>
        <CardTitle>{pix ? "Pagamento Pix" : "Dados do comprador"}</CardTitle>
        {!pix && (
          <CardDescription>
            O Mercado Pago exige CPF e nome completo para liberar o Pix. Telefone é opcional, mas aumenta a aprovação.
          </CardDescription>
        )}
      </CardHeader>
      <CardContent>{pix ? QRView : FormView}</CardContent>
    </Card>
  );
}
